/**
 * Associés — table dense + validation total 100% + coordonnées convocations.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputFR } from "@/components/ui/date-input-fr";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users2, Plus, Pencil, Trash2, AlertTriangle, CheckCircle2, Save, Building2, ShieldAlert, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  type: "physique", civilite: "M.",
  prenom: "", nom: "", raison_sociale: "", forme_juridique: "", siren: "", representant: "",
  date_naissance: "", nationalite: "Française",
  nombre_parts: 0, pourcentage: 0, valeur_apport: "",
  date_entree: "", date_sortie: "",
  adresse: "", code_postal: "", ville: "", pays: "France",
  email: "", telephone: "", notes: "",
};

export default function AssociesPanel() {
  const [rows, setRows] = useState([]);
  const [totalPct, setTotalPct] = useState(0);
  const [totalParts, setTotalParts] = useState(0);
  const [capitalReparti, setCapitalReparti] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [rbeMissing, setRbeMissing] = useState([]);  // Associés > 25% sans entrée RBE
  const [rbeSyncing, setRbeSyncing] = useState(false);
  const [sireneLoading, setSireneLoading] = useState(false);

  const lookupSirene = async () => {
    const clean = String(form.siren || "").replace(/\D/g, "").slice(0, 9);
    if (clean.length !== 9) { toast.error("SIREN invalide (9 chiffres attendus)"); return; }
    setSireneLoading(true);
    try {
      const r = await api.get(`/legal/sirene/lookup?siren=${clean}`);
      const d = r.data || {};
      setForm(f => ({
        ...f,
        siren: d.siren || clean,
        raison_sociale: d.raison_sociale || f.raison_sociale,
        forme_juridique: d.forme_juridique || f.forme_juridique,
        adresse: d.adresse || f.adresse,
        code_postal: d.code_postal || f.code_postal,
        ville: d.ville || f.ville,
      }));
      toast.success(`SIRENE OK — ${d.raison_sociale || clean}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSireneLoading(false); }
  };

  const checkRbeGaps = async (associesRows) => {
    const eligibles = (associesRows || []).filter(
      (a) => !a.date_sortie && (Number(a.pourcentage) || 0) >= 25
    );
    if (eligibles.length === 0) { setRbeMissing([]); return; }
    try {
      const r = await api.get("/legal/registers?type=beneficiaires_effectifs");
      const registered = new Set((r.data.entries || []).map((e) => e.reference));
      const missing = eligibles.filter((a) => !registered.has(a.id));
      setRbeMissing(missing);
    } catch { setRbeMissing([]); }
  };

  const syncRbe = async () => {
    setRbeSyncing(true);
    try {
      const r = await api.post("/legal/registers/beneficiaires_effectifs/sync");
      toast.success(`RBE synchronisé — ${r.data.created} entrée(s) créée(s)`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRbeSyncing(false); }
  };

  const load = async () => {
    try {
      const d = await api.get("/legal/associes");
      setRows(d.data.associes || []);
      setTotalPct(d.data.total_pourcentage || 0);
      setTotalParts(d.data.total_parts || 0);
      setCapitalReparti(!!d.data.capital_reparti);
      await checkRbeGaps(d.data.associes || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setOpenSheet(true); };
  const openEdit = (r) => {
    setForm({ ...EMPTY, ...r, valeur_apport: r.valeur_apport ?? "" });
    setEditingId(r.id);
    setOpenSheet(true);
  };

  const save = async () => {
    if (form.type === "physique" && (!form.prenom || !form.nom)) {
      toast.error("Prénom et nom requis"); return;
    }
    if (form.type === "morale" && !form.raison_sociale) {
      toast.error("Raison sociale requise"); return;
    }
    const payload = { ...form };
    if (payload.valeur_apport === "") delete payload.valeur_apport;
    else payload.valeur_apport = parseFloat(payload.valeur_apport);
    payload.nombre_parts = parseFloat(payload.nombre_parts) || 0;
    payload.pourcentage = parseFloat(payload.pourcentage) || 0;
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    try {
      if (editingId) {
        await api.patch(`/legal/associes/${editingId}`, payload);
        toast.success("Associé mis à jour");
      } else {
        await api.post("/legal/associes", payload);
        toast.success("Associé ajouté");
      }
      setOpenSheet(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet associé ?")) return;
    try { await api.delete(`/legal/associes/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const nomAssocie = (r) => r.type === "morale"
    ? `${r.raison_sociale} (${r.forme_juridique || "PM"})`
    : `${r.civilite || ""} ${r.prenom} ${r.nom}`.trim();

  return (
    <div className="space-y-4" data-testid="associes-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-slate-600">
            <span className="font-medium">{rows.length}</span> associé{rows.length > 1 ? "s" : ""}
            {" · "}
            <span className="font-mono font-medium">{totalParts.toLocaleString("fr-FR")}</span> parts
          </p>
          <Badge className={capitalReparti ? "bg-emerald-100 text-emerald-700" : totalPct > 100 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"} data-testid="capital-repartition-badge">
            {capitalReparti
              ? <><CheckCircle2 className="w-3 h-3 mr-1" />Capital réparti : 100%</>
              : <><AlertTriangle className="w-3 h-3 mr-1" />Répartition : {totalPct}% {totalPct < 100 ? `(manque ${(100 - totalPct).toFixed(2)}%)` : `(excès ${(totalPct - 100).toFixed(2)}%)`}</>}
          </Badge>
        </div>
        <Button onClick={openCreate} data-testid="add-associe-btn"><Plus className="w-4 h-4 mr-2" />Ajouter un associé</Button>
        <Button variant="outline" onClick={async () => {
          try {
            const r = await api.post("/legal/sirene/batch-refresh");
            const upd = r.data.updated_count || 0;
            const unchanged = r.data.unchanged_count || 0;
            const errs = r.data.errors_count || 0;
            toast.success(`SIRENE — ${upd} MAJ · ${unchanged} inchangé(s) · ${errs} erreur(s)`);
            load();
          } catch (e) { toast.error(apiError(e)); }
        }} data-testid="sirene-batch-refresh-btn">
          <Sparkles className="w-4 h-4 mr-1.5 text-purple-500" />Rafraîchir tous les SIREN
        </Button>
      </div>

      {rbeMissing.length > 0 && (
        <Card className="border-red-300 bg-red-50" data-testid="rbe-warning-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-900">
                  ⚠ Registre des Bénéficiaires Effectifs (RBE) incomplet — obligation légale
                </p>
                <p className="text-sm text-red-800 mt-1">
                  {rbeMissing.length} associé(s) détenant ≥ 25 % du capital n'ont pas d'entrée
                  au registre RBE. Déclaration obligatoire — art. R. 561-56 CMF (sanctions :
                  6 mois d'emprisonnement + 7 500 €).
                </p>
                <ul className="text-sm text-red-700 mt-2 ml-4 list-disc">
                  {rbeMissing.slice(0, 5).map((a) => (
                    <li key={a.id}>
                      {nomAssocie(a)} — <span className="font-mono">{(a.pourcentage || 0).toFixed(2)}%</span>
                    </li>
                  ))}
                  {rbeMissing.length > 5 && <li>+ {rbeMissing.length - 5} autres…</li>}
                </ul>
                <div className="mt-3">
                  <Button size="sm" onClick={syncRbe} disabled={rbeSyncing}
                    data-testid="rbe-sync-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
                    {rbeSyncing ? "Synchronisation…" : "Régulariser le RBE en 1 clic"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {rows.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Users2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>Aucun associé enregistré. Renseignez les associés pour générer des convocations personnalisées.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Associé</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Parts</TableHead>
                  <TableHead className="text-right">%</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`associe-row-${r.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {r.type === "morale"
                          ? <Building2 className="w-4 h-4 text-slate-400" />
                          : <Users2 className="w-4 h-4 text-slate-400" />}
                        <span className="font-medium">{nomAssocie(r)}</span>
                      </div>
                      {r.email && <p className="text-xs text-slate-500 ml-6">{r.email}</p>}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{r.type === "morale" ? "Personne morale" : "Personne physique"}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{(r.nombre_parts || 0).toLocaleString("fr-FR")}</TableCell>
                    <TableCell className="text-right font-mono font-medium">{(r.pourcentage || 0).toFixed(2)} %</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.ville || "—"}</TableCell>
                    <TableCell>
                      {r.date_sortie
                        ? <Badge className="bg-slate-200 text-slate-600 text-xs">Sorti</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-700 text-xs">Actif</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-associe-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(r.id)} data-testid={`delete-associe-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Sheet open={openSheet} onOpenChange={setOpenSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>{editingId ? "Modifier l'associé" : "Ajouter un associé"}</SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="associe-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physique">Personne physique</SelectItem>
                  <SelectItem value="morale">Personne morale</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.type === "physique" ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Civilité</Label>
                    <Select value={form.civilite} onValueChange={(v) => setForm({ ...form, civilite: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["M.", "Mme"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Prénom *</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="associe-prenom" /></div>
                  <div><Label>Nom *</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="associe-nom" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Date naissance</Label><DateInputFR value={form.date_naissance} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} /></div>
                  <div><Label>Nationalité</Label><Input value={form.nationalite} onChange={(e) => setForm({ ...form, nationalite: e.target.value })} /></div>
                </div>
              </>
            ) : (
              <>
                <div><Label>Raison sociale *</Label><Input value={form.raison_sociale} onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })} data-testid="associe-raison-sociale" /></div>
                <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                  <div>
                    <Label>SIREN <span className="text-xs text-slate-400 font-normal">— 9 chiffres, auto-completion INSEE</span></Label>
                    <Input value={form.siren}
                      onChange={(e) => setForm({ ...form, siren: e.target.value })}
                      placeholder="Ex: 552120222"
                      data-testid="associe-siren" />
                  </div>
                  <Button type="button" variant="outline" size="sm"
                    onClick={lookupSirene} disabled={sireneLoading}
                    data-testid="sirene-lookup-btn">
                    {sireneLoading
                      ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      : <Sparkles className="w-4 h-4 mr-1.5 text-purple-500" />}
                    {sireneLoading ? "…" : "Auto-remplir"}
                  </Button>
                </div>
                <div><Label>Forme juridique</Label><Input value={form.forme_juridique} onChange={(e) => setForm({ ...form, forme_juridique: e.target.value })} placeholder="SARL, SAS…" /></div>
                <div><Label>Représentant</Label><Input value={form.representant} onChange={(e) => setForm({ ...form, representant: e.target.value })} /></div>
              </>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div><Label>Parts *</Label><Input type="number" value={form.nombre_parts} onChange={(e) => setForm({ ...form, nombre_parts: e.target.value })} data-testid="associe-parts" /></div>
              <div><Label>Pourcentage % *</Label><Input type="number" step="0.01" value={form.pourcentage} onChange={(e) => setForm({ ...form, pourcentage: e.target.value })} data-testid="associe-pourcentage" /></div>
              <div><Label>Valeur apport €</Label><Input type="number" value={form.valeur_apport} onChange={(e) => setForm({ ...form, valeur_apport: e.target.value })} /></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date entrée</Label><DateInputFR value={form.date_entree} onChange={(e) => setForm({ ...form, date_entree: e.target.value })} /></div>
              <div><Label>Date sortie</Label><DateInputFR value={form.date_sortie} onChange={(e) => setForm({ ...form, date_sortie: e.target.value })} /></div>
            </div>

            <div><Label>Adresse (pour convocations)</Label><Textarea rows={2} value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Code postal</Label><Input value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} /></div>
              <div className="col-span-2"><Label>Ville</Label><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <SheetFooter className="mt-4">
            <Button onClick={save} data-testid="save-associe-btn"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
